import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { RxUpdate } from 'react-icons/rx';
import { TbSquareRoundedPlusFilled } from 'react-icons/tb';
import { toast } from 'react-toastify';
import { useAuthContext } from '../../../contexts/AuthProvider';
import useConversionActions from '../../../hooks/APIHooks/appointments/useConversionActions';
import { createEditConversionSchema } from '../../../schemas';
import { Conversion, ConversionReqBody } from '../../../types/conversion';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaInputField from '../Inputs/AcademiaInputField';
import AcademiaTextAreaField from '../Inputs/AcademiaTextAreaField';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const CreateEditConversionModal = ({
  title,
  handleModalClose,
  conversion,
}: {
  title: 'create' | 'update';
  handleModalClose: () => void;
  conversion?: Conversion;
}) => {
  // integration of API hooks here
  const { createNewConversionHandler, updateConversionHandler } =
    useConversionActions('');

  // integration of context hooks here
  const { currentUser } = useAuthContext();

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // handler function to handle create/edit conversion form submission declared here
  const createEditConversionHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      console.log(values);

      let body: ConversionReqBody = {
        name: values.name,
        minutes_per_credit: values.minutes_per_credit,
        description: values.description,
      };

      if (!conversion) {
        body = { ...body, created_by: currentUser?.username };

        await createNewConversionHandler
          .mutateAsync({ body })
          .then((res) => {
            if (res.is_success) {
              toast.success('Conversion Created!!');

              handleModalClose();
              queryClient.invalidateQueries({ queryKey: ['conversion-list'] });
            } else {
              toast.error(res.message || 'Conversion Creation Failed!!');
            }
          })
          .catch((error) => toast.error(fetchErrorMessage(error)));
      } else {
        body = { ...body, updated_by: currentUser?.username };

        await updateConversionHandler
          .mutateAsync({ conversionId: conversion.id, body })
          .then((res) => {
            if (res.is_success) {
              toast.success('Conversion Updated!!');

              handleModalClose();
              queryClient.invalidateQueries({ queryKey: ['conversion-list'] });
            } else {
              toast.error(res.message || 'Conversion Updating Failed!!');
            }
          })
          .catch((error) => toast.error(fetchErrorMessage(error)));
      }
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering create/edit conversion modal component here
  return (
    <ParentModal onClick={handleModalClose}>
      <ModalTitle titleText={`${title} conversion`} />

      <Formik
        initialValues={{
          name: conversion?.name || '',
          minutes_per_credit: conversion?.minutes_per_credit || '',
          description: conversion?.description || '',
        }}
        onSubmit={createEditConversionHandler}
        validationSchema={createEditConversionSchema}
      >
        {(props) => (
          <Form className='space-y-5'>
            <AcademiaInputField
              type='text'
              name='name'
              label='Conversion Name'
              placeholder='Enter conversion name here...'
              isRequired
            />

            <AcademiaInputField
              type='number'
              name='minutes_per_credit'
              label='Minutes Per Credit'
              placeholder='Enter minutes per credit here...'
              isRequired
            />

            <AcademiaTextAreaField
              name='description'
              label='Description'
              placeholder='Type conversion description here...'
              isRequired
            />

            <AcademiaButton
              type='submit'
              variant='premium'
              icon={
                title === 'create' ? (
                  <TbSquareRoundedPlusFilled className='size-6' />
                ) : (
                  <RxUpdate className='size-6' />
                )
              }
              isLoading={props.isSubmitting}
              fullWidth
            >
              {title} Conversion
            </AcademiaButton>
          </Form>
        )}
      </Formik>
    </ParentModal>
  );
};

export default CreateEditConversionModal;
